/**
 * Reveal timeline planner for the Marketing Roast Machine.
 *
 * Given a recommendation count and a pseudo-random number source, produces
 * the per-item delays (in milliseconds) used to stagger the reveal of each
 * recommendation on the result screen.
 *
 * This module is pure: no DOM imports, no `Date.now()`, no side effects.
 * Determinism comes from the caller-supplied `rng`, which lets tests pin
 * outcomes via `seededRng(seed)` (Property 5 — staggered reveal timeline
 * bounds).
 *
 * See `.kiro/specs/marketing-roast-machine/design.md` — "Services / Modules"
 * and Requirement 4.2.
 */

/**
 * Plan per-item reveal delays for `count` recommendations.
 *
 * Each returned delay is an integer in the closed interval [600, 1200] ms,
 * drawn uniformly from the caller-supplied `rng` (which must yield floats
 * in [0, 1)).
 *
 * - `count === 0` returns an empty array.
 * - Non-finite, non-integer, or negative `count` throws `RangeError`, so
 *   callers fail loudly instead of silently producing an empty timeline.
 *
 * @param count number of recommendations to schedule (non-negative integer)
 * @param rng   PRNG returning floats in [0, 1); use `seededRng` for tests
 * @returns     array of length `count` with each entry in [600, 1200] ms
 */
export function plan(count: number, rng: () => number): number[] {
  if (!Number.isInteger(count) || count < 0) {
    throw new RangeError(
      `plan(count): expected a non-negative integer, got ${String(count)}`,
    );
  }

  const delays: number[] = new Array<number>(count);
  for (let i = 0; i < count; i++) {
    // rng() ∈ [0, 1); multiplying by 601 and flooring yields an integer in
    // [0, 600], so 600 + that is an integer in [600, 1200] inclusive at both
    // endpoints.
    delays[i] = 600 + Math.floor(rng() * 601);
  }
  return delays;
}

/**
 * Small, deterministic PRNG (mulberry32).
 *
 * Returns a function that yields floats in [0, 1). Seeding with the same
 * integer always produces the same stream, which is what property-based
 * tests rely on for reproducible counter-examples.
 *
 * The seed is coerced to a 32-bit unsigned integer via `>>> 0`, matching
 * the canonical mulberry32 reference implementation.
 *
 * @param seed any finite number; fractional parts are truncated by `>>> 0`
 * @returns    a zero-arg function returning the next float in [0, 1)
 */
export function seededRng(seed: number): () => number {
  let state = seed >>> 0;
  return function mulberry32(): number {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
